<template>
    <div
        ref="slider"
        class="bs-multi-range"
        :class="{ 'no-labels': !props.labels || props.labels.length === 0, disabled: props.disabled }"
    >
        <div class="labels position-relative">
            <span
                v-for="label in upperLabels"
                :key="label.value"
                class="label position-absolute"
                :style="[
                    label.value > 0 && label.value < 100 ? `left: ${label.value}%` : '',
                    label.value == 100 ? 'right: 0' : '',
                    true ? 'margin-top: -1.5em' : '',
                ]"
                >{{ label.label }}</span
            >
        </div>
        <div class="track px-2">
            <div
                ref="thumbend"
                class="thumb upper"
            ></div>
            <div
                ref="thumbstart"
                class="thumb lower"
            ></div>

            <div class="track-diff"></div>
        </div>
        <div
            class="labels"
            style="position: relative"
        >
            <span
                v-for="label in lowerLabels"
                :key="label.value"
                class="label"
                :style="[
                    'position: absolute',
                    label.value > 0 && label.value < 100 ? `left: ${label.value}%; margin-left: -.5em` : '',
                    label.value == 100 ? 'right: 0' : '',
                ]"
            >
                {{ label.label }}
            </span>
        </div>
    </div>
</template>
<script lang="ts" setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { DoubleSlider } from './doubleslider';
import { Popover } from 'bootstrap';
import { randomstring } from '@/utilities/utils';
import { $tm } from '@/locize';

const props = defineProps<{
    /**
     * The selected minimum value
     */
    minvalue: number;

    /**
     * The selected maximum value
     */
    maxvalue: number;

    /**
     * The step factor
     * How much one step in the slider is worth
     * @default 1
     */
    stepfactor?: number;

    /**
     * Whether the slider is disabled or not
     * @default false
     */
    disabled?: boolean;

    /**
     * The labels to show on the slider.
     * This will wrap the value in a {{ count }} variable
     */
    poppertext?: string;

    labels?: Array<{ value: number; label: string }>;
}>();

let isInitialized = false;

/// The slider element
const slider = ref<HTMLElement | null>(null);

// The first thumb element
const thumbstart = ref<HTMLElement | null>(null);

// The second thumb element
const thumbend = ref<HTMLElement | null>(null);

// Property to hold the popper state for the thumbstart
let startPopper: Popover | null = null;

// Helper property to address the start popper DOM element
let startPopperClass = randomstring('inputrange-');

// Property to hold the popper state for the thumbend
let endPopper: Popover | null = null;

// Helper property to address the end popper DOM element
let endPopperClass = randomstring('inputrange-');

/**
 * The lower labels
 * These are the main labels and we show only lower labels if there is 3 or less
 */
const lowerLabels = computed(() => {
    if (!props.labels) return [];

    if (props.labels.length <= 3) {
        return props.labels;
    }

    // Get every odd label
    return props.labels?.filter((_, i) => i % 2 === 0);
});

/**
 * We show the upper labels if there are more than 3 labels. We do this to prevent the slider from becoming too crowded
 */
const upperLabels = computed(() => {
    if (!props.labels) return [];

    if (props.labels.length <= 3) {
        return [];
    }

    // Get every even label
    return props.labels?.filter((_, i) => i % 2 === 1);
});

/**
 * The events we emit
 */
const emit = defineEmits<{
    (e: 'update:minvalue', value: number): void;
    (e: 'update:maxvalue', value: number): void;
    (e: 'change', value: { min: number; max: number }): void;
}>();

// Internal reference to the min value
const _minvalue = computed<number>({
    get: () => props.minvalue,
    set: (val) => emit('update:minvalue', val),
});

// Internal reference to the max value
const _maxvalue = computed<number>({
    get: () => props.maxvalue,
    set: (val) => emit('update:maxvalue', val),
});

/**
 * Handle the change event and update the modelvalues and emit the change event
 */
const onChange = (event: any) => {
    let newMin = Math.ceil(event.detail.target.getMin() * (props.stepfactor ?? 1));
    let newMax = Math.ceil(event.detail.target.getMax() * (props.stepfactor ?? 1));

    if (newMin !== _minvalue.value) {
        _minvalue.value = newMin;

        setPopperContent(newMin, 'start');
    } else if (newMax !== _maxvalue.value) {
        _maxvalue.value = newMax;

        setPopperContent(newMax, 'end');
    }

    emit('change', { min: newMin, max: newMax });
};

/**
 * Whenever one of the thumbs are moved we show the corresponding popper
 * @param event
 */
const onMoveStart = (event: any) => {
    if (event.detail.target.el === thumbstart.value) {
        startPopper?.show();
    } else if (event.detail.target.el === thumbend.value) {
        endPopper?.show();
    }
};

/**
 * Whenever one of the thumbs are moved we hide the corresponding popper
 * @param event
 */
const onMoveEnd = (event: any) => {
    if (event.detail.target.el === thumbstart.value) {
        startPopper?.hide();
    } else if (event.detail.target.el === thumbend.value) {
        endPopper?.hide();
    }
};

/**
 * Set the popper content.
 * We do this instead of using the popper's setContent method because it closes the popper on update. This is much smoother
 * @param content The content value to set.
 */
const setPopperContent = (content: number, popper: 'start' | 'end') => {
    let el = null;
    if (popper === 'start') {
        startPopper?.update();
        el = document.querySelector(`.${startPopperClass} .popover-body`);
    } else {
        endPopper?.update();
        el = document.querySelector(`.${endPopperClass} .popover-body`);
    }
    if (el) el.innerHTML = props.poppertext ? $tm(props.poppertext, content) : content.toString();
};

let sl: DoubleSlider | null = null;

const initializeSlider = () => {
    if (slider.value && !isInitialized) {
        sl = new DoubleSlider(slider.value, 0, 100, 16, true, true, true);
        sl.el.addEventListener('change', onChange);
        sl.el.addEventListener('start', onMoveStart);
        sl.el.addEventListener('stop', onMoveEnd);

        isInitialized = true;
    }
};

watch(
    () => props.disabled,
    (newVal) => {
        if (!newVal) {
            initializeSlider();
        } else {
            // TODO: Destroy slider
        }
    },
);

/**
 * Initialize the slider
 */
onMounted(() => {
    // Make sure the slider is available
    if (slider.value && !props.disabled) {
        initializeSlider();
    }

    // Set the popper for the thumbs
    startPopper = new Popover(thumbstart.value!, {
        content: ' ',
        trigger: 'manual',
        placement: 'top',
        customClass: startPopperClass,
    });

    // Set the popper for the thumbs
    endPopper = new Popover(thumbend.value!, {
        content: ' ',
        trigger: 'manual',
        placement: 'top',
        customClass: endPopperClass,
    });
});

onUnmounted(() => {
    startPopper?.dispose();
    endPopper?.dispose();

    sl?.destroy();
});
</script>
